import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { PageHeader } from '@/client/components/common/PageHeader';
import { Badge } from '@/client/components/ui/badge';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { DatePicker } from '@/client/components/ui/date-picker';
import { Label } from '@/client/components/ui/label';
import { Textarea } from '@/client/components/ui/textarea';
import { useAdminGuard } from '@/client/hooks/useAdminGuard';
import { trpc } from '@/client/utils/trpc';

export const Route = createFileRoute('/_app/finance/new')({
  component: FinanceNewPage,
});

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatPreviewAmount(amount: string, type: 'INCOME' | 'OUTCOME'): string {
  const n = Number.parseFloat(amount.replace(',', '.'));
  if (Number.isNaN(n) || amount === '') return '—';
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
  return `${type === 'INCOME' ? '+' : '–'}€${formatted}`;
}

function formatPreviewDate(isoDate: string): string {
  if (!isoDate) return '—';
  const d = new Date(`${isoDate}T00:00:00`);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function formatRecentAmount(amount: number, type: 'INCOME' | 'OUTCOME'): string {
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
  return `${type === 'INCOME' ? '+' : '–'}€${formatted}`;
}

function FinanceNewPage() {
  useAdminGuard();
  const navigate = useNavigate();

  const [type, setType] = useState<'INCOME' | 'OUTCOME'>('OUTCOME');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data: categories = [] } = trpc.finance.listCategories.useQuery();
  const { data: latestRecords = [] } = trpc.finance.latestRecords.useQuery();
  const utils = trpc.useUtils();

  const createMutation = trpc.finance.createRecord.useMutation({
    onSuccess: () => {
      utils.finance.listRecords.invalidate();
      utils.finance.latestRecords.invalidate();
      utils.finance.listCategories.invalidate();
      utils.finance.monthlySummary.invalidate();
      utils.finance.dashboardSummary.invalidate();
      navigate({
        to: '/finance',
        search: { type: 'ALL', month: currentMonth(), category: '' },
      });
    },
    onError: (err) => setError(err.message),
  });

  const effectiveCategory = customCategory.trim() || selectedCategory;

  function handleSave() {
    const amountNum = Number.parseFloat(amount.replace(',', '.'));
    if (Number.isNaN(amountNum) || amountNum <= 0) {
      setError('Enter a valid positive amount.');
      return;
    }
    if (!effectiveCategory) {
      setError('Select or enter a category.');
      return;
    }
    setError(null);
    createMutation.mutate({
      date,
      amount: amountNum,
      type,
      category: effectiveCategory,
      description: note.trim() || effectiveCategory,
    });
  }

  const previewAmount = formatPreviewAmount(amount, type);
  const previewDate = formatPreviewDate(date);

  return (
    <div className="space-y-6">
      <PageHeader
        title="New entry"
        actions={
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              navigate({
                to: '/finance',
                search: { type: 'ALL', month: currentMonth(), category: '' },
              })
            }
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        }
      />

      <div className="mx-auto grid max-w-[1100px] grid-cols-[1fr_320px] gap-6">
        {/* ── Left: entry form ─────────────────────────────────────── */}
        <Card>
          <CardHeader className="border-b px-6 py-4">
            <CardTitle className="text-base">Entry details</CardTitle>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Log an income or outcome for the ledger.
            </p>
          </CardHeader>

          <CardContent className="flex flex-col gap-5 p-6">
            {/* Type */}
            <fieldset className="space-y-2 border-0 p-0">
              <legend className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Type
              </legend>
              <div className="flex rounded-md border">
                {(['INCOME', 'OUTCOME'] as const).map((t, i) => (
                  <button
                    key={t}
                    type="button"
                    aria-pressed={type === t}
                    onClick={() => setType(t)}
                    className={[
                      'flex-1 py-2 text-sm font-medium transition-colors',
                      i === 0
                        ? 'rounded-l-[calc(var(--radius)-1px)]'
                        : 'rounded-r-[calc(var(--radius)-1px)]',
                      type === t
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    ].join(' ')}
                  >
                    {t === 'INCOME' ? 'Income' : 'Outcome'}
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Date + Amount */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="new-date">Date</Label>
                <DatePicker id="new-date" value={date} onChange={setDate} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-amount">Amount (EUR)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    €
                  </span>
                  <Input
                    id="new-amount"
                    className="pl-7 tabular-nums"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Category */}
            <fieldset className="space-y-2 border-0 p-0">
              <legend className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                Category
              </legend>
              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => {
                        setSelectedCategory(c);
                        setCustomCategory('');
                      }}
                      className={[
                        'h-8 rounded-md border px-3 text-sm font-medium transition-colors',
                        selectedCategory === c && !customCategory.trim()
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-foreground hover:bg-muted',
                      ].join(' ')}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <Input
                maxLength={80}
                placeholder={
                  categories.length > 0 ? 'Or type a custom category…' : 'Type a category…'
                }
                value={customCategory}
                onChange={(e) => {
                  setCustomCategory(e.target.value);
                  setSelectedCategory('');
                }}
              />
            </fieldset>

            {/* Note */}
            <div className="space-y-2">
              <Label htmlFor="new-note">Note</Label>
              <Textarea
                id="new-note"
                maxLength={500}
                placeholder="Add a note…"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            {/* Footer */}
            <div className="flex items-center gap-3 border-t pt-4">
              <div className="flex-1" />
              <Button type="button" onClick={handleSave} disabled={createMutation.isPending}>
                <Check className="mr-2 h-4 w-4" />
                {createMutation.isPending ? 'Saving…' : 'Save entry'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* ── Right: sidebar ───────────────────────────────────────── */}
        <div className="flex flex-col gap-4">
          {/* Preview */}
          <Card className="p-5">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Preview
            </p>
            <div className="flex flex-col gap-2.5 rounded-md bg-muted p-4">
              <div className="flex items-center justify-between">
                {type === 'INCOME' ? (
                  <Badge className="gap-1.5 border-0 bg-good-bg text-good">
                    <span className="h-1.5 w-1.5 rounded-full bg-good" />
                    Income
                  </Badge>
                ) : (
                  <Badge variant="outline">Outcome</Badge>
                )}
                <span className="tabular-nums text-xs text-muted-foreground">{previewDate}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium">{effectiveCategory || '—'}</span>
                <span
                  className={`text-2xl font-semibold tabular-nums ${type === 'INCOME' ? 'text-good' : ''}`}
                >
                  {previewAmount}
                </span>
              </div>
              {note && <p className="text-xs text-muted-foreground">{note}</p>}
            </div>
          </Card>

          {/* Recent */}
          {latestRecords.length > 0 && (
            <Card className="overflow-hidden">
              <div className="border-b px-4 py-3">
                <p className="text-sm font-semibold">Recent</p>
              </div>
              <div>
                {latestRecords.slice(0, 5).map((r, i) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setType(r.type);
                      setAmount(String(r.amount));
                      setSelectedCategory(r.category);
                      setCustomCategory('');
                      setNote(r.description);
                      setDate(r.date);
                    }}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/60 ${i < Math.min(latestRecords.length, 5) - 1 ? 'border-b' : ''}`}
                  >
                    <span
                      className={`text-sm ${r.type === 'INCOME' ? 'text-good' : 'text-muted-foreground'}`}
                    >
                      {r.type === 'INCOME' ? '↑' : '↓'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{r.description}</p>
                      <p className="text-xs text-muted-foreground">{r.category}</p>
                    </div>
                    <span
                      className={`tabular-nums text-sm font-semibold ${r.type === 'INCOME' ? 'text-good' : ''}`}
                    >
                      {formatRecentAmount(r.amount, r.type)}
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
